import { Exception } from "@zxing/library";
import { createContext, ReactNode, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { PlanModel, Subscription, SubscriptionDetailModel, SubscriptionStatusEnum } from "../types/subscription";
import { useApi } from "./ApiContext";
import useToast from "./ToastContext";
import useUserContext from "./UserContext";

export const PAYMENT_SESSION_ID_STORAGE_KEY = "PAYMENT_SESSION_ID";
export const PAYMENT_SESSION_STORAGE_KEY = "PAYMENT_SESSION";
export const PAYMENT_SUBSCRIPTION_ID_STORAGE_KEY = "PAYMENT_SUBSCRIPTION_ID";

type subscriptionByIdResult = {
	subscription: SubscriptionDetailModel | undefined;
	isLoading: boolean;
	isRefetching: boolean;
	isDowngradeScheduled: boolean | undefined;
};

type subscriptionsByNodeIdResult = {
	subscriptions: Subscription[] | undefined;
	isLoading: boolean;
	isRefetching: boolean;
};

export type SubscriptionContextType = {
	subscriptions: Subscription[] | undefined;
	available: number;
	getSubscriptionById: (id: string) => subscriptionByIdResult;
	getSubscriptionsByNodeId: (nodeId: string) => subscriptionsByNodeIdResult;
	cancelSubscription: (subscriptionId: string) => Promise<void>;
	reactivateSubscription: (subscriptionId: string) => Promise<void>;
	updatePaymentMethod: (subscriptionId: string) => Promise<void>;
	addNodeToSubscription: (nodeId: string, subscriptionId: string) => Promise<void>;
	removeNodeFromSubscription: (nodeId: string, subscriptionId: string) => Promise<void>;
	activeSubscriptions: Subscription[] | undefined;
	totalSubscriptionsCount: number;
	page: number;
	setPage: (page: number) => void;
	size: number;
	setSize: (size: number) => void;
	isLoadingSubscriptions: boolean;
	plans: PlanModel[] | undefined;
	isLoadingPlans: boolean;
	refetchSubscriptions: () => void;
};

export const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);
SubscriptionContext.displayName = "SubscriptionContext";

export const SubscriptionContextProvider: React.FC<{ children: ReactNode | ReactNode[] }> = ({ children }) => {
	const { t } = useTranslation();
	const toast = useToast();
	const { subscription: api } = useApi();
	const { isAccountOwner } = useUserContext();

	const currency = "";

	const [page, setPage] = useState<number>(0);
	const [size, setSize] = useState<number>(10);

	const {
		data: subscriptionsResult,
		isLoading: isLoadingSubscriptions,
		refetch: refetchSubscriptions,
	} = useQuery(
		["subscriptions", { page: page, size: size }],
		async () => {
			const fetchedSubscriptions = await api.getSubscriptions({
				page: page ?? 0,
				size: size ?? 10,
			});
			if (!fetchedSubscriptions.isSuccess) throw new Exception(fetchedSubscriptions.message);
			return fetchedSubscriptions.result;
		},
		{
			staleTime: 500,
			// The subscription endpoint is owner-only; invited users would always get 403.
			enabled: isAccountOwner,
		}
	);

	const subscriptions = useMemo(() => subscriptionsResult?.data, [subscriptionsResult?.data]);
	const totalSubscriptionsCount = useMemo(
		() => subscriptionsResult?.totalCount ?? 0,
		[subscriptionsResult?.totalCount]
	);

	function getSubscriptionById(id: string) {
		const {
			data: subscription,
			isLoading,
			isRefetching,
		} = useQuery(["subscription-" + id], async () => {
			const subscription = await api.getSubscriptionById(id);
			return subscription.result;
		});

		const isDowngradeScheduled = subscription?.schedule?.some(({ typeString }) => typeString === "Downgrade");

		return useMemo(
			() => ({ subscription, isLoading, isRefetching, isDowngradeScheduled }),
			[subscription, isLoading, isRefetching, isDowngradeScheduled]
		);
	}

	function getSubscriptionsByNodeId(nodeId: string) {
		const {
			data: subscriptions,
			isLoading,
			isRefetching,
		} = useQuery(["subscriptions-node-" + nodeId], async () => {
			const subscriptions = await api.getSubscriptionsByNodeId(nodeId);
			return subscriptions.result;
		});

		return useMemo(() => ({ subscriptions, isLoading, isRefetching }), [subscriptions, isLoading, isRefetching]);
	}

	const activeSubscriptions = subscriptionsResult?.data.filter(
		(subscription) => subscription.status === SubscriptionStatusEnum.ACTIVE
	);

	const { data: plans, isLoading: isLoadingPlans } = useQuery(["availablePlans-" + currency], async () => {
		const fetchedPlans = await api.getAvailablePlans({ currency: currency ?? "" });
		if (!fetchedPlans.isSuccess) throw new Exception(fetchedPlans.message);
		return fetchedPlans.result;
	});

	async function addNodeToSubscription(nodeId: string, subscriptionId: string) {
		try {
			const response = await api.addNodeToSubscription({ nodeId, subscriptionId });
			if (response.isSuccess) {
				toast.toastSuccess(t("buoysPage.buoyAddedToSubscription"));
			} else {
				toast.toastError(response.message);
			}
		} catch (e: any) {
			toast.toastError(e.message);
		}
	}

	async function removeNodeFromSubscription(nodeId: string, subscriptionId: string) {
		try {
			const response = await api.removeNodeFromSubscription({ nodeId, subscriptionId });
			if (response.isSuccess) {
				toast.toastSuccess(t("buoysPage.buoyRemovedFromSubscription"));
			} else {
				toast.toastError(response.message);
			}
		} catch (e) {
			toast.toastError(t("buoysPage.weEncounteredAnError"));
		}
	}

	async function cancelSubscription(subscriptionId: string) {
		try {
			const response = await api.cancelSubscription(subscriptionId);
			if (response.isSuccess) {
				toast.toastSuccess(t("subscriptionPage.cancelSuccessful"));
			} else {
				toast.toastError(t("subscriptionPage.cancelFailed"));
			}
		} catch (e) {
			toast.toastError(t("subscriptionPage.cancelFailed"));
		}
	}

	async function reactivateSubscription(subscriptionId: string) {
		try {
			const response = await api.reactivateSubscription(subscriptionId);
			if (response.isSuccess) {
				toast.toastSuccess(t("subscriptionPage.reactivateSuccess"));
			} else {
				toast.toastError(t("subscriptionPage.reactivateFailed"));
			}
		} catch (e) {
			toast.toastError(t("subscriptionPage.reactivateFailed"));
		}
	}

	async function updatePaymentMethod(subscriptionId: string) {
		try {
			const response = await api.updatePaymentMethod(subscriptionId);
			if (response.isSuccess && response.result) {
				window.location.href = response.result.url;
			} else {
				toast.toastError(t("payment.paymentMethodFailed"));
			}
		} catch (e) {
			toast.toastError(t("payment.paymentMethodFailed"));
		}
	}

	const available: number =
		subscriptionsResult?.data.reduce((sum, subscription) => {
			return sum + subscription.available;
		}, 0) ?? 0;

	return (
		<SubscriptionContext.Provider
			value={{
				subscriptions,
				available,
				getSubscriptionById,
				getSubscriptionsByNodeId,
				cancelSubscription,
				reactivateSubscription,
				updatePaymentMethod,
				addNodeToSubscription,
				removeNodeFromSubscription,
				activeSubscriptions,
				totalSubscriptionsCount,
				page,
				setPage,
				size,
				setSize,
				isLoadingSubscriptions,
				plans,
				isLoadingPlans,
				refetchSubscriptions,
			}}
		>
			{children}
		</SubscriptionContext.Provider>
	);
};

function useSubscriptionContext() {
	const context = useContext(SubscriptionContext);
	if (!context) {
		throw new Error("useSubscriptionContext() must be used inside a SubscriptionContext provider");
	}
	return context;
}

export default useSubscriptionContext;
